// src/rms/components/RequireRole.jsx
// ============================================================================
// Route guards for the four surfaces
// ============================================================================
// A client-side guard is a courtesy, not a security control. The real boundary
// is RLS: rms_is_operator_member / rms_is_advertiser_member / rms_is_ad_admin /
// rms_is_platform_admin. Someone who edits this component in devtools reaches a
// screen that renders no data, because the database refuses to return any.
//
// The value here is telling an authorised person why they are being turned
// away, rather than showing them an empty page.
// ============================================================================

import React, { useEffect, useState } from "react";
import { Navigate, useLocation, Link } from "react-router-dom";
import db, { isLocalBackend } from "../data";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../config/supabaseClient";
import { Banner } from "./ui";
import i18n from "../i18n";

/**
 * These guards wrap RmsProvider rather than sitting inside it, so neither
 * translator hook is available here. The i18next instance works anywhere.
 */
// Takes VALUES as well as a fallback. It used to accept only (key, fallback)
// and silently drop anything passed third, so every interpolated string in this
// file rendered its placeholder raw -- the access-denied banner literally read
// "You don't have {area} access." A signature that quietly discards an argument
// fails where nobody is looking, which on this screen is somebody already
// locked out and now being shown a template.
const t = (key, fallback, values) =>
  i18n.t(key, { defaultValue: fallback, ...(values || {}) });

/** Which membership check each role needs, and where to send a stranger. */
const ROLE_CHECKS = {
  advertiser: {
    label: "advertiser",
    signInPath: "/advertiser",
    check: async () => {
      const { data } = await supabase.rpc("rms_current_advertiser_id");
      return !!data;
    },
  },
  ad_admin: {
    label: "ad sales",
    signInPath: "/signin",
    check: async () => {
      const { data } = await supabase.rpc("rms_is_ad_admin");
      return !!data;
    },
  },
  platform_admin: {
    label: "site admin",
    signInPath: "/signin",
    check: async () => {
      const { data } = await supabase.rpc("rms_is_platform_admin");
      return !!data;
    },
  },
};

function Centered({ children }) {
  return (
    <div className="rms-root" style={{ display: "grid", placeItems: "center", minHeight: "70vh", padding: 24 }}>
      <div style={{ maxWidth: 460, width: "100%" }}>{children}</div>
    </div>
  );
}

/**
 * Gates a route on an authenticated session and, optionally, a role.
 *
 * `role` omitted → any signed-in user (the operator surfaces, which are then
 * scoped to their own operator by RLS).
 */
export function RequireRole({ role = null, children }) {
  const { isAuthenticated, loading, authEnforced } = useAuth();
  const location = useLocation();

  const [roleState, setRoleState] = useState(role && authEnforced ? "checking" : "allowed");

  useEffect(() => {
    if (!role || !authEnforced || !isAuthenticated) return;
    let cancelled = false;

    (async () => {
      try {
        const ok = await ROLE_CHECKS[role].check();
        if (!cancelled) setRoleState(ok ? "allowed" : "denied");
      } catch {
        if (!cancelled) setRoleState("denied");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [role, authEnforced, isAuthenticated]);

  // Local backend has no auth server — everything is open, by design.
  if (!authEnforced) return children;

  if (loading) {
    return (
      <Centered>
        <p style={{ color: "var(--rms-muted-strong)", textAlign: "center" }}>Checking your session…</p>
      </Centered>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/signin" replace state={{ from: location.pathname }} />;
  }

  if (role && roleState === "checking") {
    return (
      <Centered>
        <p style={{ color: "var(--rms-muted-strong)", textAlign: "center" }}>Checking access…</p>
      </Centered>
    );
  }

  if (role && roleState === "denied") {
    return (
      <Centered>
        <Banner tone="warn">
          <div>
            <strong>
              {t("requireRole.noAccess", "You don't have {area} access.", {
                area: ROLE_CHECKS[role].label,
              })}
            </strong>
            <p style={{ margin: "6px 0 0" }}>
              {t("requireRole.thisAreaIsRestricted", "This area is restricted. If it should be available to you, ask an administrator to grant it — the database, not this page, decides.")}
            </p>
          </div>
        </Banner>
        <div style={{ textAlign: "center", marginTop: 14 }}>
          <Link className="rms-btn rms-btn--ghost rms-btn--sm" to="/rms">
            {t("requireRole.backToTheApp", "Back to the app")}
          </Link>
        </div>
      </Centered>
    );
  }

  return children;
}

/**
 * Operator surfaces: needs a session AND a provisioned operator record.
 * A signed-in user with no operator goes to the vertical picker.
 */
export function RequireOperator({ children }) {
  const [checked, setChecked] = useState(false);
  const [hasOperator, setHasOperator] = useState(false);
  const { isAuthenticated, authEnforced, loading } = useAuth();

  useEffect(() => {
    if (authEnforced && (loading || !isAuthenticated)) return;
    let cancelled = false;

    db.getOperatorForUser().then((r) => {
      if (cancelled) return;
      setHasOperator(!!r.data);
      setChecked(true);
    });

    return () => {
      cancelled = true;
    };
  }, [authEnforced, isAuthenticated, loading]);

  if (!checked && (!authEnforced || isAuthenticated)) {
    return (
      <Centered>
        <p style={{ color: "var(--rms-muted-strong)", textAlign: "center" }}>Loading your account…</p>
      </Centered>
    );
  }

  if (checked && !hasOperator) return <Navigate to="/rms/start" replace />;

  return children;
}

export { isLocalBackend };
export default RequireRole;
